import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  AppState,
  LensMode,
  MonteCarloResult,
  ProjectResultRecord,
  SimulationResult,
  WorkerRequest,
  WorkerResponse,
} from '../types'

interface WorkerRunState {
  kind: 'simulation' | 'monte-carlo' | null
  running: boolean
  progress: number
  message: string
  error: string | null
}

const initialRunState: WorkerRunState = {
  kind: null,
  running: false,
  progress: 0,
  message: '',
  error: null,
}

export function useSimulationWorker() {
  const workerRef = useRef<Worker | null>(null)
  const activeJobRef = useRef<string | null>(null)
  const activeFingerprintRef = useRef<string | null>(null)
  const [runState, setRunState] = useState<WorkerRunState>(initialRunState)
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null)
  const [monteCarloResult, setMonteCarloResult] = useState<MonteCarloResult | null>(null)
  const [simulationFingerprint, setSimulationFingerprint] = useState<string | null>(null)
  const [monteCarloFingerprint, setMonteCarloFingerprint] = useState<string | null>(null)

  useEffect(() => {
    const worker = new Worker(new URL('../workers/simulation.worker.ts', import.meta.url), {
      type: 'module',
    })
    workerRef.current = worker
    worker.addEventListener('message', (event: MessageEvent<WorkerResponse>) => {
      const response = event.data
      if (response.jobId !== activeJobRef.current) return
      if (response.type === 'progress') {
        setRunState((current) => ({
          ...current,
          progress: Math.max(current.progress, response.progress),
          message: response.message,
        }))
        return
      }
      if (response.type === 'simulation-result') {
        setSimulationResult(response.result)
        setSimulationFingerprint(activeFingerprintRef.current)
        setRunState((current) => ({
          ...current,
          running: false,
          progress: 1,
          message: 'Simulation complete',
        }))
      } else if (response.type === 'monte-carlo-result') {
        setMonteCarloResult(response.result)
        setMonteCarloFingerprint(activeFingerprintRef.current)
        setRunState((current) => ({
          ...current,
          running: false,
          progress: 1,
          message: 'Tolerance analysis complete',
        }))
      } else if (response.type === 'cancelled') {
        setRunState((current) => ({
          ...current,
          running: false,
          message: 'Cancelled',
        }))
      } else if (response.type === 'error') {
        setRunState((current) => ({
          ...current,
          running: false,
          error: response.error,
          message: 'Run failed',
        }))
      }
      activeJobRef.current = null
      activeFingerprintRef.current = null
    })
    return () => worker.terminate()
  }, [])

  const startSimulation = useCallback(
    (state: AppState, mode: LensMode, fingerprint: string) => {
      if (!workerRef.current) return
      if (activeJobRef.current) {
        workerRef.current.postMessage({ type: 'cancel', jobId: activeJobRef.current } satisfies WorkerRequest)
      }
      const jobId = crypto.randomUUID()
      activeJobRef.current = jobId
      activeFingerprintRef.current = fingerprint
      setRunState({
        kind: 'simulation',
        running: true,
        progress: 0,
        message: 'Preparing ray trace',
        error: null,
      })
      workerRef.current.postMessage({
        type: 'simulate',
        jobId,
        payload: { state, mode },
      } satisfies WorkerRequest)
    },
    [],
  )

  const startMonteCarlo = useCallback((state: AppState, fingerprint: string) => {
    if (!workerRef.current) return
    if (activeJobRef.current) {
      workerRef.current.postMessage({ type: 'cancel', jobId: activeJobRef.current } satisfies WorkerRequest)
    }
    const jobId = crypto.randomUUID()
    activeJobRef.current = jobId
    activeFingerprintRef.current = fingerprint
    setRunState({
      kind: 'monte-carlo',
      running: true,
      progress: 0,
      message: 'Preparing tolerance trials',
      error: null,
    })
    workerRef.current.postMessage({
      type: 'monte-carlo',
      jobId,
      payload: { state },
    } satisfies WorkerRequest)
  }, [])

  const cancel = useCallback(() => {
    if (!workerRef.current || !activeJobRef.current) return
    workerRef.current.postMessage({
      type: 'cancel',
      jobId: activeJobRef.current,
    } satisfies WorkerRequest)
    setRunState((current) => ({ ...current, message: 'Cancelling after current chunk…' }))
  }, [])

  const replaceEvidence = useCallback((evidence: {
    simulation: ProjectResultRecord<SimulationResult> | null
    tolerance: ProjectResultRecord<MonteCarloResult> | null
  }) => {
    if (workerRef.current && activeJobRef.current) {
      workerRef.current.postMessage({
        type: 'cancel',
        jobId: activeJobRef.current,
      } satisfies WorkerRequest)
    }
    activeJobRef.current = null
    activeFingerprintRef.current = null
    setSimulationResult(evidence.simulation?.result ?? null)
    setSimulationFingerprint(evidence.simulation?.inputFingerprint ?? null)
    setMonteCarloResult(evidence.tolerance?.result ?? null)
    setMonteCarloFingerprint(evidence.tolerance?.inputFingerprint ?? null)
    setRunState(initialRunState)
  }, [])

  const clearSimulation = useCallback(() => {
    setSimulationResult(null)
    setSimulationFingerprint(null)
  }, [])

  const clearMonteCarlo = useCallback(() => {
    setMonteCarloResult(null)
    setMonteCarloFingerprint(null)
  }, [])

  return {
    runState,
    simulationResult,
    monteCarloResult,
    simulationFingerprint,
    monteCarloFingerprint,
    startSimulation,
    startMonteCarlo,
    cancel,
    replaceEvidence,
    clearSimulation,
    clearMonteCarlo,
  }
}
